import React, {
	ReactNode,
	useState,
	useCallback,
	useMemo,
	useRef,
	useEffect,
} from 'react';
import CollectionPreferences, {
	CollectionPreferencesProps,
} from '@amzn/awsui-components-console/collection-preferences';
import Header from '@amzn/awsui-components-console/header';
import Pagination from '@amzn/awsui-components-console/pagination';
import PropertyFilter, {
	PropertyFilterProps,
} from '@amzn/awsui-components-console/property-filter';
import Button from '@amzn/awsui-components-console/button';
import Table from '@amzn/awsui-components-console/table';
import Container from '@amzn/awsui-components-console/container';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import Popover from '@amzn/awsui-components-console/popover';
import Link from '@amzn/awsui-components-console/link';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import Spinner from '@amzn/awsui-components-console/spinner';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import {
	COLLECTION_PREFERENCES,
	DEFAULT_PAGE_SIZE,
	FILTERING_PROPERTIES,
	VISIBLE_CONTENT,
	getSubTaskExecutionDataURL,
} from './subTaskConstants';
import { SubTaskStatus } from 'src/constants/commonEnums';
import { PropertyFilteri18nStrings } from 'src/constants/ComponentConstants';
import { TableWithNoData } from 'src/components/commonComponents/table/tableWithNoData';
import { KeyValue } from 'src/components/commonComponents/KeyValue';
import { useCollection } from '@amzn/awsui-collection-hooks';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import {
	getDateAndTimeFromEpoch,
	sortedTaskStatus,
} from 'src/utils/CommonUtils';
import {
	TT_ENDPOINT,
	SIM_ENDPOINT,
	SNOW_NXT_WORKFLOW_ENDPOINT,
	MCM_END_POINT,
} from 'src/config';
import {
	ListSubTaskExecutionsResponse,
	SubTaskExecutionData,
	TaskExecutionData,
} from '@amzn/snow-nxt-workflow-service-typescript-client';
import { usePaginatedCallback } from 'src/hooks/usePaginatedCallback';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import SubWorkflowExecutionView from './SubWorkflowExecutionView';
import {
	createLabelValueArray,
	createColumnDefinitions,
} from 'src/utils/labelValueUtils';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import './SubTaskExecutionView.scss';

const STATUS_FILTERING_OPTIONS: PropertyFilterProps.FilteringOption[] =
	Object.values(SubTaskStatus).map((s) => ({
		propertyKey: 'status',
		value: s,
	}));

function getNotificationBaseUrl(taskType: string): string {
	const urlMap: Record<string, string> = {
		TTTask: TT_ENDPOINT,
		SIMTask: SIM_ENDPOINT,
		CreateSimTask: SIM_ENDPOINT,
		MCMCreationTask: MCM_END_POINT,
	};
	return urlMap[taskType] || '';
}

const ValueWithLabel: React.FC<
	React.PropsWithChildren<{ label: ReactNode }>
> = ({ label, children }) => (
	<div>
		<Box variant="awsui-key-label">{label}</Box>
		<div>{children}</div>
	</div>
);


function allNotificationIdsMatch(subTasks: SubTaskExecutionData[]): boolean {
	if (subTasks.length === 0) return false;
	const ids = subTasks.map((s) => s.notificationId);

	return ids.every((id) => id && id.trim() !== '') && new Set(ids).size === 1;
}

function countByStatus(
	items: SubTaskExecutionData[],
	statuses: string[]
): number {
	return items.filter((item) => item.status && statuses.includes(item.status))
		.length;
}

function getNotificationLink(
	taskType: string | undefined,
	notificationId: string | undefined
): React.ReactElement {
	if (!notificationId || !taskType) return <></>;
	const baseUrl = getNotificationBaseUrl(taskType);

	if (!baseUrl) return <>{notificationId}</>;
	const separator = taskType === 'MCMCreationTask' ? '' : '/';

	return (
		<Link href={`${baseUrl}${separator}${notificationId}`} external>
			{notificationId}
		</Link>
	);
}

function getServerFilterValue(
	tokens: readonly PropertyFilterProps.Token[],
	propertyKey: string,
	operator = '='
): string {
	const token = tokens.find(
		(t) => t.propertyKey === propertyKey && t.operator === operator
	);
	return token?.value || '';
}

export interface SubTaskExecutionViewProps {
	workflowId: string;
	workflowExecutionId: string;
	taskDetails: Partial<TaskExecutionData>;
	show?: boolean;
	summary?: boolean;
	taskOutput?: string | object;
	isLoadingTaskOutput?: boolean;
}

export default function SubTaskExecutionView({
	workflowId,
	workflowExecutionId,
	taskDetails,
	show = true,
	summary = true,
	taskOutput = '',
	isLoadingTaskOutput = false,
}: SubTaskExecutionViewProps) {
	const isSubWorkflowTask = taskDetails.taskType === SUB_WORKFLOW_TASK_TYPE;
	const [query, setQuery] = useState<PropertyFilterProps.Query>({
		tokens: [],
		operation: 'and',
	});

	const serverStatusFilter = useMemo(
		() => getServerFilterValue(query.tokens, 'status'),
		[query.tokens]
	);

	const serverNameFilter = useMemo(
		() => getServerFilterValue(query.tokens, 'subTaskExecutionName'),
		[query.tokens]
	);

	const serverFiltersRef = useRef({ status: serverStatusFilter, name: serverNameFilter });

	useEffect(() => {
		serverFiltersRef.current = { status: serverStatusFilter, name: serverNameFilter };
	}, [serverStatusFilter, serverNameFilter]);

	const [preferences, setPreferences] = useState<
		Partial<CollectionPreferencesProps.Preferences>
	>({
		pageSize: DEFAULT_PAGE_SIZE,
		visibleContent: VISIBLE_CONTENT,
		wrapLines: false,
		stripedRows: getDefaultStripedRows(),
	});

	const prevServerStatusRef = useRef(serverStatusFilter);
	const prevServerNameRef = useRef(serverNameFilter);
	
	const clientSideTokens = useMemo(
		() => query.tokens.filter((t) => t.propertyKey !== 'status' && t.propertyKey !== 'subTaskExecutionName'),
		[query.tokens]
	);

	const {
		currentPageData: subTaskExecutionData,
		allFetchedData: allFetchedSubTasks,
		isLoading: isSubTaskExecutionLoading,
		error: subTaskExecutionResponseError,
		currentPageIndex,
		pagesCount,
		hasNextPage,
		nextPage: loadNext,
		previousPage: loadPrevious,
		updatePageSize,
	} = usePaginatedCallback<SubTaskExecutionData, ListSubTaskExecutionsResponse>(
		{
			pageSize: preferences.pageSize || DEFAULT_PAGE_SIZE,
			skipInitialFetch: isSubWorkflowTask,
			buildRequest: (
				_pageIndex,
				continuationToken,
				pageSize = DEFAULT_PAGE_SIZE
			) => ({
				path: getSubTaskExecutionDataURL(
					workflowId,
					workflowExecutionId,
					taskDetails.taskExecutionId
				),
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				queryParams: {
					count: pageSize,
					...(continuationToken && { continuationToken }),
					...(serverFiltersRef.current.status && {
						status: serverFiltersRef.current.status,
					}),
					...(serverFiltersRef.current.name && {
						subTaskExecutionName: serverFiltersRef.current.name,
					}),
				},
			}),
			transformResponse: (response: ListSubTaskExecutionsResponse) => {
				const { subTaskExecutions = [] } = response || {};

				let transformedSubTasks: SubTaskExecutionData[] = subTaskExecutions;

				if (allNotificationIdsMatch(subTaskExecutions)) {
					transformedSubTasks = [
						{
							...subTaskExecutions[0],
							subTaskExecutionName: taskDetails.taskName,
						},
					];
				}

				return {
					items: transformedSubTasks,
					continuationToken: response.continuationToken,
				};
			},
		}
	);

	useEffect(() => {
		const statusChanged = prevServerStatusRef.current !== serverStatusFilter;
		const nameChanged = prevServerNameRef.current !== serverNameFilter;

		if (statusChanged || nameChanged) {
			prevServerStatusRef.current = serverStatusFilter;
			prevServerNameRef.current = serverNameFilter;
			updatePageSize(preferences.pageSize || DEFAULT_PAGE_SIZE);
		}
	}, [serverStatusFilter, serverNameFilter, updatePageSize, preferences.pageSize]);

	const loadPreferencesData = useCallback(
		(detail: Partial<CollectionPreferencesProps.Preferences>) => {
			setPreferences(detail);
			updatePageSize(detail.pageSize || DEFAULT_PAGE_SIZE);
		},
		[updatePageSize]
	);

	const COLUMN_DEFINITIONS = useMemo(
		() =>
			createColumnDefinitions(
				[
					'status',
					'subTaskExecutionName',
					'notificationId',
					'taskExecutionId',
					'taskRunId',
					'subTaskExecutionId',
					'attemptNo',
					'lastUpdatedTime',
				],
				{
					status: {
						id: 'SubTaskStatus',
						header: 'Sub Task Status',
						minWidth: 140,
						cell: (item: SubTaskExecutionData) => (
							<div>
								{item.infoMessage ? (
									<div>
										<ShowSnowStatus status={item.status || ''} />
										<Popover content={item.infoMessage}>
											<Button iconName="status-info" variant="icon" />
										</Popover>
									</div>
								) : (
									<ShowSnowStatus status={item.status || ''} />
								)}
							</div>
						),
						sortingField: 'status',
						sortingComparator: (a, b) =>
							sortedTaskStatus(
								(a as SubTaskExecutionData).status,
								(b as SubTaskExecutionData).status
							),
					},
					subTaskExecutionName: {
						sortingField: 'subTaskExecutionName',
					},
					notificationId: {
						sortingField: 'notificationId',
						cell: (item: SubTaskExecutionData) => (
							<div>
								{getNotificationLink(taskDetails.taskType, item.notificationId)}
							</div>
						),
					},
				},
				{
					lastUpdatedTime: getDateAndTimeFromEpoch,
				}
			),
		[taskDetails.taskType]
	);

	const TASK_DETAILS_FOOTER_MAP = useMemo(
		() =>
			createLabelValueArray(taskDetails, [
				'startTime',
				'entities',
				'taskId',
				'completionTime',
				'taskDefinitionVersion',
				'attempt',
				'lastUpdatedBy',
			]),
		[taskDetails]
	);

	const TASK_DETAILS_HEADER_MAP = useMemo(
		() =>
			createLabelValueArray(taskDetails, [
				'taskName',
				'taskType',
				'status',
				'taskExecutionId',
			]),
		[taskDetails]
	);

	const taskOutputDisplay = useMemo(() => {
		if (isLoadingTaskOutput) {
			return (
				<Box textAlign="center" padding="s">
					<Spinner size="normal" />
					<Box color="text-status-inactive" margin={{ left: 's' }}>
						Loading task output...
					</Box>
				</Box>
			);
		}

		if (!taskOutput) {
			return <div className="task-output-empty">No output available</div>;
		}

		const content =
			typeof taskOutput === 'object'
				? JSON.stringify(taskOutput, null, 2)
				: taskOutput;

		return <pre className="task-output-content">{content}</pre>;
	}, [taskOutput, isLoadingTaskOutput]);

	const TASK_DETAILS_STATUS_HEADER_MAP = useMemo(
		() => [
			{
				label: <ShowSnowStatus status="TOTAL SUB TASKS" />,
				value: allFetchedSubTasks.length,
			},
			{
				label: <ShowSnowStatus status="COMPLETED" />,
				value: countByStatus(allFetchedSubTasks, ['COMPLETED', 'CREATED']),
			},
			{
				label: <ShowSnowStatus status="RUNNING" />,
				value: countByStatus(allFetchedSubTasks, ['RUNNING', 'CREATING', 'SENDING', 'PENDING_REVIEW', 'SCHEDULED_FOR_DEPLOYMENT']),
			},
			{
				label: <ShowSnowStatus status="FAILED" />,
				value: countByStatus(allFetchedSubTasks, ['FAILED']),
			},
			],
		[allFetchedSubTasks]
	);

	const { items, collectionProps, propertyFilterProps, paginationProps } =
		useCollection(subTaskExecutionData, {
			propertyFiltering: {
				filteringProperties: FILTERING_PROPERTIES,
				filteringFunction: () => true,
				empty: (
					<TableWithNoData
						message={
							subTaskExecutionResponseError || 'No instances to display.'
						}
					/>
				),
				noMatch: <TableWithNoData message="No results found for your query" />,
			},
			pagination: { pageSize: preferences.pageSize },
			sorting: {
				defaultState: { sortingColumn: COLUMN_DEFINITIONS[0] },
			},
		});

	const totalFilteredCount =
		clientSideTokens.length === 0 ? allFetchedSubTasks.length : items.length;

	if (!show || !summary) return null;

	return (
		<SpaceBetween size="l">
			{taskDetails.childWorkflowExecutionId && (
				<Link
					external
					href={`#/findCycle?cycleId=${taskDetails.childWorkflowExecutionId}&workflowType=${taskDetails.childWorkflowId}`}
					target="_blank"
					data-testid="child-execution-link"
				>
					View sub-workflow execution
				</Link>
			)}
			<Container
				header={<Header variant="h2">Task Details</Header>}
				footer={
					<ExpandableSection header="Task Properties" variant="footer">
						<ColumnLayout columns={4} variant="text-grid" borders="vertical">
							{TASK_DETAILS_FOOTER_MAP.map((item, index) => (
								<KeyValue key={index} label={item.label}>
									{item.value}
								</KeyValue>
							))}
						</ColumnLayout>
					</ExpandableSection>
				}
			>
				<ColumnLayout columns={4} variant="text-grid">
					{TASK_DETAILS_HEADER_MAP.map((item, index) => (
						<KeyValue key={index} label={item.label}>
							{item.value}
						</KeyValue>
					))}
					{!isSubWorkflowTask &&
						TASK_DETAILS_STATUS_HEADER_MAP.map((item, index) => (
							<ValueWithLabel key={index} label={item.label}>
								{item.value}
							</ValueWithLabel>
						))}
				</ColumnLayout>
			</Container>
			{isSubWorkflowTask ? (
				<SubWorkflowExecutionView
					childWorkflowId={taskDetails.childWorkflowId}
					childWorkflowExecutionId={taskDetails.childWorkflowExecutionId}
				/>
			) : (
			<Container
				header={
					<Header variant="h2" counter={`(${totalFilteredCount})`}>
						Sub Task List
					</Header>
				}
			>
				{subTaskExecutionResponseError && (
					<Alert
						type="error"
						header={subTaskExecutionResponseError}
						dismissible={false}
					/>
				)}
				<Table
					data-testid="subtaskTable"
					{...collectionProps}
					loadingText="Loading resources"
					columnDefinitions={COLUMN_DEFINITIONS}
					items={items}
					visibleColumns={preferences.visibleContent}
					wrapLines={preferences.wrapLines}
					stripedRows={preferences.stripedRows}
					stickyHeader
					loading={isSubTaskExecutionLoading}
					variant="borderless"
					pagination={
						<Pagination
							{...paginationProps}
							onNextPageClick={loadNext}
							openEnd={hasNextPage}
							currentPageIndex={currentPageIndex}
							pagesCount={pagesCount}
							onPreviousPageClick={loadPrevious}
						/>
					}
					preferences={
						<CollectionPreferences
							onConfirm={({ detail }) => loadPreferencesData(detail)}
							{...COLLECTION_PREFERENCES}
							preferences={preferences}
						/>
					}
					filter={
						<PropertyFilter
							{...propertyFilterProps}
							query={query}
							onChange={({ detail }) => setQuery(detail)}
							filteringOptions={STATUS_FILTERING_OPTIONS}
							filteringProperties={FILTERING_PROPERTIES}
							i18nStrings={{
								...PropertyFilteri18nStrings,
								filteringPlaceholder: 'Filter sub tasks by property',
							}}
							expandToViewport
						/>
					}
				/>
			</Container>
			)}
			{!isSubWorkflowTask && (
				<ExpandableSection
					header={<Header variant="h2">Task Output</Header>}
					defaultExpanded={true}
					variant="container"
				>
					<div className="task-output-container">{taskOutputDisplay}</div>
				</ExpandableSection>
			)}
		</SpaceBetween>
	);
}
